import { Context } from "./context.js";
import { createSlice } from "@reduxjs/toolkit";
import React, { useState, useEffect, useRef } from "react";
import * as RB from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedoAlt, faUndoAlt } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch,useStore } from "react-redux";
export const rotToolSlice = createSlice({
    name: "rotTool",
    initialState: {
        centerX: "",
        centerY: "",
        angle: "",
    },
    reducers: {
        updateCenter: (state, action) => {
            const newX = action.payload.centerX;
            const newY = action.payload.centerY;
            state.centerX = newX;
            state.centerY = newY;
        },
        updateAngle: (state, action) => {
            state.angle = action.payload.angle;
        },
        initState: (state, action) => {
            const w = action.payload.width;
            const h = action.payload.height;
            state.centerX = Math.round(w / 2);
            state.centerY = Math.round(h / 2);
            state.angle = 0;
        },
    },
});
const { updateCenter, updateAngle, initState } = rotToolSlice.actions;
export class RotToolCnvControls {
    /**@type {Context} */
    context;
    /**@type {import("redux").Store} */
    store;
    #isDrag = false;
    #isAngl = false;
    #startVector;
    #endVector;
    rad = 6;
    listeners = {};
    static rotCtrl = null;
    constructor(context, store) {
        this.context = context;
        this.store = store;
        this.listeners = {
            mouseup: this.mUp,
            mousedown: this.mDown,
            mouseleave: this.mLeave,
            mousemove: this.mMove,
        };
    }
    static getInstance(context, store) {
        if (this.rotCtrl === null) {
            this.rotCtrl = new RotToolCnvControls(context, store);
        }
        return this.rotCtrl;
    }
    initState = () => {
        this.store.dispatch(
            initState({
                width: this.context.offScrCnv.width,
                height: this.context.offScrCnv.height,
            })
        );
    };
    getCenterPt = () => {
        const centerX = this.store.getState().rotTool.centerX;
        const centerY = this.store.getState().rotTool.centerY;
        return [centerX, centerY];
    };
    getAngle = () => {
        const angle = this.store.getState().rotTool.angle;
        return angle;
    };
    #calcVLen = (vector) => {
        return Math.sqrt(vector[0] * vector[0] + vector[1] * vector[1]);
    };
    #calcAngle = () => {
        let dot_product =
            this.#startVector[0] * this.#endVector[0] +
            this.#startVector[1] * this.#endVector[1];
        let cross_product =
            this.#startVector[0] * this.#endVector[1] -
            this.#startVector[1] * this.#endVector[0];
        let start_len = this.#calcVLen(this.#startVector);
        let end_len = this.#calcVLen(this.#endVector);
        let cos = dot_product / (start_len * end_len);
        return (Math.sign(cross_product) * Math.acos(cos) * 180) / Math.PI;
    };
    #drawViewRotation = () => {
        let centerPt = this.getCenterPt();
        let [viewX, viewY] = this.context.orToView(centerPt);
        let angleDeg = this.getAngle();
        let angle = (angleDeg / 180) * Math.PI;
        let tr = this.context.cnvCtx.getTransform();
        this.context.cnvCtx.setTransform(1, 0, 0, 1, 0, 0);
        this.context.cnvCtx.translate(viewX, viewY);
        this.context.cnvCtx.clearRect(
            viewX,
            viewY,
            this.context.cnv.width,
            this.context.cnv.height
        );
        this.context.cnvCtx.rotate(angle);
        this.context.cnvCtx.translate(-viewX, -viewY);

        this.context.curM = this.context.cnvCtx.getTransform();
        this.context.updateImage();
    };
    drawControls = () => {
        let centerPt = this.getCenterPt();
        let [viewX, viewY] = this.context.orToView(centerPt);
        let tr = this.context.cnvCtx.getTransform();
        this.context.cnvCtx.setTransform(1, 0, 0, 1, 0, 0);
        this.context.cnvCtx.beginPath();
        this.context.cnvCtx.arc(viewX, viewY, this.rad, 2 * Math.PI, false);
        this.context.cnvCtx.fillStyle = "green";
        this.context.cnvCtx.fill();
        this.context.cnvCtx.lineWidth = 1;
        this.context.cnvCtx.strokeStyle = "#000033";
        this.context.cnvCtx.stroke();
        this.context.cnvCtx.setTransform(tr);
    };
    updateImage = () => {
        this.#drawViewRotation();
        this.drawControls();
    };
    mDown = (event) => {
        this.drawControls();
        if (event.buttons != 1) return;
        let centerPt = this.getCenterPt();
        let [viewX, viewY] = this.context.orToView(centerPt);
        let lt = [viewX - this.rad, viewY - this.rad];
        let rb = [viewX + this.rad, viewY + this.rad];
        if (
            event.offsetX > lt[0] &&
            event.offsetX < rb[0] &&
            event.offsetY > lt[1] &&
            event.offsetY < rb[1]
        ) {
            this.#isDrag = true;
        } else {
            this.#startVector = [event.offsetX - viewX, event.offsetY - viewY];
            this.#isAngl = true;
        }
    };
    mMove = (event) => {
        let centerPt = this.getCenterPt();
        let [viewX, viewY] = this.context.orToView(centerPt);
        if (this.#isDrag) {
            let pt = this.context.viewToOr([event.offsetX, event.offsetY]);
            this.store.dispatch(
                updateCenter({ centerX: pt[0], centerY: pt[1] })
            );
        } else if (centerPt[0] != null && centerPt[0] != "" && this.#isAngl) {
            this.#endVector = [event.offsetX - viewX, event.offsetY - viewY];
            let angle = this.#calcAngle();
            this.store.dispatch(updateAngle({ angle: angle }));
        }
    };
    mUp = (event) => {
        if (this.#isDrag) {
            this.#isDrag = false;
        }
        if (this.#isAngl) {
            this.#isAngl = false;
        }
    };
    mLeave = () => {
        this.#isDrag = false;
        this.#isAngl = false;
    };
}
export const RotProps = (props) => {
    /**@type {Context} */
    const context = props.context;
    const centerX = useSelector((state) => state.rotTool.centerX);
    const centerY = useSelector((state) => state.rotTool.centerY);
    const angle = useSelector((state) => state.rotTool.angle);
    const centerXRef = useRef(null);
    const centerYRef = useRef(null);
    const angleRef = useRef(null);
    const store = useStore();
    const dispatch = useDispatch();
    useEffect(()=>{
        centerXRef.current.addEventListener("change",(event)=>{
            changeCenterX(event);
            centerXRef.current.value = store.getState().rotTool.centerX;
        });
        centerYRef.current.addEventListener("change",(event)=>{
            changeCenterY(event);
            centerYRef.current.value = store.getState().rotTool.centerY;
        });
        angleRef.current.addEventListener("change",(event)=>{
            changeAngle(event);
            angleRef.current.value = store.getState().rotTool.angle;
        });
    },[]);
    useEffect(()=>{
        centerXRef.current.value = Math.round(centerX);
        centerYRef.current.value = Math.round(centerY);
        angleRef.current.value = Math.round(angle);
    },[centerX,centerY,angle])
    const changeCenterX = (event) => {
        let newX = Number(event.target.value);
        dispatch(updateCenter({ centerX: newX, 
            centerY: store.getState().rotTool.centerY }));
    };
    const changeCenterY = (event) => {
        let newY = Number(event.target.value);
        dispatch(updateCenter({ centerX: store.getState().rotTool.centerX,
             centerY: newY }));
    };
    const changeAngle = (event) => {
        let angle = Number(event.target.value);
        dispatch(updateAngle({ angle: angle }));
    };
    const calcNewCornerPts = (trMat) => {
        let newAngPts = [],
            angPts = [
                [0, 0],
                [context.offScrCnv.width, 0],
                [context.offScrCnv.width, context.offScrCnv.height],
                [0, context.offScrCnv.height],
            ];
        for (let i = 0; i < angPts.length; ++i) {
            let newX =
                angPts[i][0] * trMat.a + angPts[i][1] * trMat.c + trMat.e;
            let newY =
                angPts[i][0] * trMat.b + angPts[i][1] * trMat.d + trMat.f;
            newAngPts.push([newX, newY]);
        }
        return newAngPts;
    };
    const calcNewSize = (newAngPts) => {
        let newW = 0,
            newH = 0;
        for (let i = 0; i < newAngPts.length; ++i) {
            let newX = newAngPts[i][0],
                newY = newAngPts[i][1];
            if (newX > context.offScrCnv.width || newX < 0) {
                newW += Math.abs(newX % context.offScrCnv.width);
            }
            if (newY > context.offScrCnv.height || newY < 0) {
                newH += Math.abs(newY % context.offScrCnv.height);
            }
        }
        return [
            Math.round(newW + context.offScrCnv.width),
            Math.round(newH + context.offScrCnv.height),
        ];
    };
    const calcOuterRectangle = () => {
        let imgData = context.offScrCnvCtx.getImageData(
            0,
            0,
            context.offScrCnv.width,
            context.offScrCnv.height
        );
        let w = imgData.width,
            h = imgData.height,
            data = imgData.data;
        let lt = [w, h],
            rb = [0, 0];
        let idx, check;
        for (let i = 0; i < w; ++i) {
            for (let j = 0; j < h; ++j) {
                idx = j * w * 4 + i * 4;
                check =
                    (data[idx] === 0 &&
                        data[idx + 1] === 0 &&
                        data[idx + 2] === 0) ||
                    data[idx + 3] === 0;
                if (check) {
                    continue;
                }
                lt[0] = lt[0] > i ? i : lt[0];
                lt[1] = lt[1] > j ? j : lt[1];
                rb[0] = rb[0] < i ? i : rb[0];
                rb[1] = rb[1] < j ? j : rb[1];
            }
        }
        return [lt, rb];
    };
    const resetHandle = (event) => {
        dispatch(
            initState({
                width: context.offScrCnv.width,
                height: context.offScrCnv.height,
            })
        );
    };
    const applyHandle = (event) => {
        let radAngle = (angle / 180) * Math.PI;
        // Save image to curCnv
        context.curCnvCtx.drawImage(context.offScrCnv, 0, 0);
        // Reset canvas transform
        context.offScrCnvCtx.setTransform(1, 0, 0, 1, 0, 0);
        // Clear whole canvas
        context.offScrCnvCtx.clearRect(
            0,
            0,
            context.offScrCnv.width,
            context.offScrCnv.height
        );
        // Translate canvas origin to rotation center
        context.offScrCnvCtx.translate(centerX, centerY);
        // Make rotation
        context.offScrCnvCtx.rotate(radAngle);
        // Translate canvas origin back
        context.offScrCnvCtx.translate(-centerX, -centerY);
        // Save current transform matrix
        let trMat = context.offScrCnvCtx.getTransform();
        // Because rotated image is no longer fit inside current dimensions
        // we calculate new corner points coordinates to be able to fit our image
        let newAngPts = calcNewCornerPts(trMat);
        // Calculate new canvas size based on new angle points
        let newSize = calcNewSize(newAngPts);
        // Calculate translation on canvas with new size
        let mvX = Math.round(Math.min(...newAngPts.map((x) => x[0])));
        let mvY = Math.round(Math.min(...newAngPts.map((x) => x[1])));
        // Update canvas size. This operation resets transformation matrix
        // but it is saved in trMat variable.
        context.offScrCnv.width = newSize[0];
        context.offScrCnv.height = newSize[1];
        // Apply calculated translation
        context.offScrCnvCtx.translate(Math.abs(mvX), Math.abs(mvY));
        // Multiply current transformation matrix with matrix in trMat
        context.offScrCnvCtx.transform(
            trMat.a,
            trMat.b,
            trMat.c,
            trMat.d,
            trMat.e,
            trMat.f
        );
        context.offScrCnvCtx.drawImage(context.curCnv, 0, 0);
        context.updateImage();
        // offScrCnvCtx.drawImage(,0,0);
        // cnvCtx.getImageData.
        // resetControls();
        resetHandle();
        context.putToHistory();
    };
    const fitHandle = (event) => {
        console.time("Fit");
        let [lt, rb] = calcOuterRectangle();
        console.timeEnd("Fit");
        let newW = rb[0] - lt[0],
            newH = rb[1] - lt[1];
        context.curCnvCtx.drawImage(context.offScrCnv, 0, 0);
        context.offScrCnvCtx.clearRect(
            0,
            0,
            context.offScrCnv.width,
            context.offScrCnv.height
        );
        context.offScrCnv.width = newW;
        context.offScrCnv.height = newH;
        // this.context.offScrCnvCtx.drawImage(this.context.curCnv,lt[0],lt[1]);
        context.offScrCnvCtx.translate(-lt[0], -lt[1]);
        context.offScrCnvCtx.drawImage(context.curCnv, 0, 0);

        context.updateImage();
        context.putToHistory();
    };
    const rotate90 = (direction) => {
        context.curCnvCtx.drawImage(context.offScrCnv, 0, 0);
        let newSize = [context.offScrCnv.height, context.offScrCnv.width];
        context.offScrCnv.width = newSize[0];
        context.offScrCnv.height = newSize[1];
        switch (direction) {
            case "counter-clockwise":
                context.offScrCnvCtx.translate(0, newSize[1]);
                context.offScrCnvCtx.rotate(-Math.PI / 2);
                break;
            case "clockwise":
                context.offScrCnvCtx.translate(newSize[0], 0);
                context.offScrCnvCtx.rotate(Math.PI / 2);
                break;
        }
        context.offScrCnvCtx.drawImage(context.curCnv, 0, 0);
        context.updateImage();
        resetHandle();
        context.putToHistory();
    };

    const form = (
        <RB.Form>
            <RB.Form.Group className="toolprop">
                <RB.Form.Row>
                    <RB.Col>
                        <RB.Form.Label size="sm">Центр X:</RB.Form.Label>
                    </RB.Col>
                    <RB.Col md={7}>
                        <RB.Form.Control
                            ref={centerXRef}
                            // onChange={changeCenterX}
                            // value={centerX}
                            type="number"
                            id="rot-x"
                            size="sm"
                        ></RB.Form.Control>
                    </RB.Col>
                </RB.Form.Row>
                <RB.Form.Row>
                    <RB.Col>
                        <RB.Form.Label size="sm">Центр Y:</RB.Form.Label>
                    </RB.Col>
                    <RB.Col md={7}>
                        <RB.Form.Control
                            ref={centerYRef}
                            // onChange={changeCenterY}
                            // value={centerY}
                            type="number"
                            id="rot-y"
                            size="sm"
                        ></RB.Form.Control>
                    </RB.Col>
                </RB.Form.Row>
                <RB.Form.Row>
                    <RB.Col>
                        <RB.Form.Label size="sm">Угол:</RB.Form.Label>
                    </RB.Col>
                    <RB.Col md={7}>
                        <RB.Form.Control
                            ref={angleRef}
                            // onChange={changeAngle}
                            // value={angle}
                            type="number"
                            id="rot-angle"
                            size="sm"
                        ></RB.Form.Control>
                    </RB.Col>
                </RB.Form.Row>
                <RB.Form.Row>
                    <RB.Button
                        id="rot-fit"
                        onClick={fitHandle}
                        className="btn-block"
                        size="sm"
                    >
                        Обрезать до содержимого
                    </RB.Button>
                </RB.Form.Row>
                <RB.Form.Row className="button-row">
                    <RB.Button
                        className="rot-button"
                        id="rot-left"
                        onClick={() => rotate90("counter-clockwise")}
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faUndoAlt} />
                    </RB.Button>
                    <RB.Button
                        className="rot-button"
                        id="rot-right"
                        onClick={() => rotate90("clockwise")}
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faRedoAlt} />
                    </RB.Button>
                </RB.Form.Row>
                <RB.Form.Row className="button-row">
                    <RB.Button id="rot-apply" onClick={applyHandle} size="sm">
                        Применить
                    </RB.Button>
                    <RB.Button id="rot-reset" onClick={resetHandle} size="sm">
                        Сброс
                    </RB.Button>
                </RB.Form.Row>
            </RB.Form.Group>
        </RB.Form>
    );
    return form;
};
